import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  merge,
  of,
} from 'rxjs';
import { catchError, map, share, startWith, switchMap } from 'rxjs/operators';
import {
  ApplicationPermissionsApiService,
  CategoriesGraphqlService,
  CrossReferenceGraphqlService,
  CrossreferencesApiService,
} from '../../services/api';
import { noDefaultErrorHandler } from '../../services/interceptors/error-handler/operators/no-default-error-handler.operator';
import { NotyService } from '../../services/noty.service';
import { Features } from '../../types/features';
import {
  CrossReferencePermissions,
  PermissionContext,
} from '../../types/permissions';
import {
  CrossReference,
  CrossReferencesOpinionOption,
  Product,
} from '../../types/product';
import { CrossReferenceType, User } from '../../types/types';
import { Sizes } from '../modules';
import { CrossReferencesManagementService } from './cross-references-management.service';
import { CrossReferencesModalParams } from './cross-references-modal-params';
import moment from 'moment';

@Component({
  selector: 'app-cross-references-modal',
  templateUrl: './cross-references-modal.component.html',
  styleUrls: ['./cross-references-modal.component.scss'],
  standalone: false,
})
export class CrossReferencesModalComponent implements OnInit {
  /**
   * @important
   * We might have a redundancy with observables.
   */
  // This merges the initial loaded product as well as the updated product.
  currentProduct$!: Observable<Product>;
  // This is for updating the product.
  currentProductSubject$ = new Subject<Product>();

  // Used for grid data. Crosses from product object.
  crossReferences$!: Observable<CrossReference[]>;
  searchTermSubject$ = new BehaviorSubject<string>('');
  loadingIndicatorSizes = Sizes;

  canUpdateCrosses = false;
  features?: Features;
  userAccount?: User;

  crossReferenceOpinionOptions = [] as CrossReferencesOpinionOption[];
  crossReferencePermissions!: CrossReferencePermissions;
  categoryId!: number;
  productIds!: number[];
  variant?: string;
  filterDate?: string;
  categorizationSchemeId?: number;

  @Input() modalParams$: BehaviorSubject<CrossReferencesModalParams> =
    new BehaviorSubject({} as CrossReferencesModalParams);

  isLoadingSearchResults = false;
  isShowingSearchResults = false;
  modalResult$ = this.currentProductSubject$.pipe(
    map((crossProduct) => ({
      initialProduct: this.modalParams$.value.initialProduct,
      crossProduct,
    })),
  );
  private destroyRef = inject(DestroyRef);

  constructor(
    public activeModal: NgbActiveModal,
    private categoriesGraphqlService: CategoriesGraphqlService,
    private crossReferenceGraphqlService: CrossReferenceGraphqlService,
    private crossReferencesApiService: CrossreferencesApiService,
    private crossReferencesManagementService: CrossReferencesManagementService,
    private appPermService: ApplicationPermissionsApiService,
    private notyService: NotyService,
  ) {}

  ngOnInit() {
    /**
     * @important
     * This chunk of code needs to be visited.
     * We might be making an unnecessary use of too many observables.
     */

    // Resolve params and cached data that is necessary for remote requests.
    this.modalParams$
      .pipe(
        switchMap((modalParams) => {
          this.userAccount = modalParams.userAccount;
          this.features = modalParams.featureFlags;
          this.categoryId = modalParams.categoryId;
          this.productIds = modalParams.productIds;
          this.filterDate = modalParams.filterDate
            ? moment(modalParams?.filterDate).format('DD-MM-YYYY')
            : '';
          this.categorizationSchemeId = modalParams.categorizationSchemeId;
          this.variant = modalParams.variant;

          if (modalParams.initialProduct) {
            this.currentProduct$ = of(modalParams.initialProduct);
          }

          // Combine all remote requests.
          return combineLatest([
            this.categoriesGraphqlService.getProduct(
              {
                productId: this.productIds[0],
                filterDate: this.filterDate,
                categorizationSchemeId: this.categorizationSchemeId,
                variant: this.variant,
              },
              this.features,
            ),
            this.appPermService.getPermissions(this.userAccount?.userId, [
              { context: PermissionContext.CROSS_REFERENCE },
            ]),
            this.getOpinionOptions(),
            this.crossReferenceGraphqlService.getVizientReviewOptions(),
          ]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        ([product, permissions, opinionOptions, vizientReviewOptions]) => {
          this.crossReferencePermissions =
            permissions.CROSS_REFERENCE as CrossReferencePermissions;
          this.canUpdateCrosses =
            this.crossReferencePermissions.CHANGE_CROSS_REFERENCE_OPINION?.hasAccess;
          if (this.userAccount?.isGPO)
            this.crossReferenceOpinionOptions = vizientReviewOptions;
          else this.crossReferenceOpinionOptions = opinionOptions;

          this.currentProduct$ = merge(
            of(product),
            this.currentProductSubject$,
          );

          const searchCrosses$: Observable<Product[]> =
            this.searchTermSubject$.pipe(
              switchMap((searchTerm: string) => {
                if (searchTerm) {
                  this.isLoadingSearchResults = true;
                  if (this.features?.CONSUME_PRODUCT_DATA_MD_CAT_964488) {
                    return this.categoriesGraphqlService.getCrossesByDistributor(
                      {
                        searchValue: searchTerm,
                        categoryId: this.productIds[0],
                        distributorId: this.userAccount?.isSeller
                          ? this.userAccount?.organization.id
                          : undefined,
                        variant: this.variant,
                      },
                    );
                  } else {
                    return this.categoriesGraphqlService.getCrosses(
                      searchTerm,
                      this.productIds[0],
                      this.userAccount?.isSeller
                        ? this.userAccount?.organization.id
                        : undefined,
                    );
                  }
                } else {
                  return of([]);
                }
              }),
            );

          this.crossReferences$ = combineLatest([
            this.currentProduct$,
            searchCrosses$,
          ]).pipe(
            map(([currentProduct, searchCrosses]) => {
              this.isLoadingSearchResults = false;
              if (this.searchTermSubject$.value) {
                this.isShowingSearchResults = true;
                return searchCrosses.map((product) => {
                  // Check to see if the cross is already added
                  const crossReference = currentProduct.crossReferences?.find(
                    (currentCross) => {
                      return currentCross?.toProduct?.id === product.id;
                    },
                  );
                  // Replace Cross if already added
                  return (
                    crossReference || ({ toProduct: product } as CrossReference)
                  );
                });
              } else {
                this.isShowingSearchResults = false;
                return currentProduct.crossReferences || [];
              }
            }),
            noDefaultErrorHandler(),
            catchError((error) => {
              this.isLoadingSearchResults = false;
              this.notyService.error(error.message ? error.message : error);
              return this.currentProduct$.pipe(
                map((p) => p.crossReferences || []),
              );
            }),
            share({ connector: () => new ReplaySubject(1) }),
          );
        },
      );
  }

  getOpinionOptions(): Observable<CrossReferencesOpinionOption[]> {
    if (this.features?.CQVA_FUNCTIONAL_ASSESSMENT_UPDATE_CAT_967295) {
      return this.crossReferenceGraphqlService.getOpinionOptions();
    } else {
      return this.crossReferencesApiService.getOpinionOptions();
    }
  }

  clearSearch() {
    this.searchTermSubject$.next('');
  }

  showNoEquivalentCrossButton(currentProduct: Product) {
    return (
      this.userAccount?.isSeller &&
      !this.searchTermSubject$.value &&
      !this.hasNoEquivalentCross(currentProduct)
    );
  }

  hasNoEquivalentCross(currentProduct: Product) {
    const crossReferences = currentProduct.crossReferences;
    return crossReferences?.some((crossReference: CrossReference) => {
      return (
        crossReference.createdByOrg.id === this.userAccount?.organization.id &&
        crossReference.crossReferenceType === CrossReferenceType.NO_MATCH
      );
    });
  }

  markNoEquivalentCross(currentProduct: Product) {
    const optimisticProduct =
      this.crossReferencesManagementService.optimisticNoEquivalent(
        currentProduct,
      );
    this.currentProductSubject$.next(optimisticProduct);

    return this.crossReferenceGraphqlService
      .addNoMatch(currentProduct.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  promoteCross(currentProduct: Product, promotedCross: CrossReference) {
    const optimisticProduct =
      this.crossReferencesManagementService.optimisticPromoteCross(
        currentProduct,
        promotedCross,
      );
    this.currentProductSubject$.next(optimisticProduct);

    this.crossReferenceGraphqlService
      .promoteCross(currentProduct.id, promotedCross.toProduct.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  removeCross(currentProduct: Product, removedCross: CrossReference) {
    const optimisticProduct =
      this.crossReferencesManagementService.optimisticRemoveCross(
        currentProduct,
        removedCross,
      );
    this.currentProductSubject$.next(optimisticProduct);

    const toProductId =
      removedCross.crossReferenceType !== CrossReferenceType.NO_MATCH
        ? removedCross?.toProduct.id
        : 0;

    this.crossReferenceGraphqlService
      .removeCross(currentProduct.id, toProductId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  addCrossReference(
    currentProduct: Product,
    addedCrossReference: CrossReference,
  ) {
    const optimisticProduct =
      this.crossReferencesManagementService.optimisticAddCross(
        currentProduct,
        addedCrossReference,
      );
    const crossIndex = optimisticProduct.crossReferences!.findIndex(
      (crossReference) =>
        !crossReference.id || addedCrossReference.id === crossReference.id,
    );
    const addedCross = optimisticProduct.crossReferences![crossIndex];
    this.currentProductSubject$.next(optimisticProduct);

    // Make API request but first send optimistic response.
    this.crossReferenceGraphqlService
      .addCross(currentProduct.id, addedCross.toProduct.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((reponse) => {
          optimisticProduct.crossReferences![crossIndex].id =
            reponse.data?.createCrossReference.id!;
          return optimisticProduct;
        }),
        // Must be last or after map to work properly.
        startWith(optimisticProduct),
      )
      .subscribe((product) => {
        this.currentProductSubject$.next(product);
        this.searchTermSubject$.next('');
      });
  }

  updateOpinion(
    currentProduct: Product,
    {
      crossReference,
      willDeleteCross,
    }: { crossReference: CrossReference; willDeleteCross: boolean },
  ) {
    let productToUpdate = currentProduct;
    if (willDeleteCross) {
      productToUpdate =
        this.crossReferencesManagementService.optimisticRemoveCross(
          currentProduct,
          crossReference,
        );
    } else {
      productToUpdate =
        this.crossReferencesManagementService.optimisticUpdateCross(
          currentProduct,
          crossReference,
        );
    }

    this.currentProductSubject$.next(productToUpdate);
  }

  closeModal() {
    // It's possible we might not need this.
    this.activeModal.close();
  }
}
